/**
 * Page Registry
 * 
 * Centralized registry of all dashboard pages and their charts.
 * Used to provide context to the chatbot about what the user is viewing.
 */

import { PageDefinition } from '../types/chatContext';

/**
 * Registry mapping page IDs to their definitions
 */
export const PAGE_REGISTRY: Record<string, PageDefinition> = {
  'brand-tracker': {
    id: 'brand-tracker',
    name: 'Brand Tracker',
    description: 'Overview of brand performance across social media platforms. Shows engagement trends, brand rankings, and top-performing content.',
    charts: [
      {
        id: 'brand-love',
        name: 'Brand Love Index',
        purpose: 'Shows Brand Love score trends over time for each brand. Chart Y-axis shows 0-60 range. Higher values = stronger engagement.',
        dataSource: 'topics_{org_id}.feather (calculate Brand Love from this, see system instructions)',
        metrics: ['brand_love'],
        calculationDescription: 'Brand Love is calculated as a 30-day rolling engagement score from topics data, then transformed for display.',
        transformation: 'Calculate from topics file: group by Brand+Date, 30-day rolling sum of post_engscore / 30, multiply by 40, then transform: display = sign(raw) * sqrt(abs(raw)) * 1000 / 13000. Chart shows ~0-60 range.'
      },
      {
        id: 'brand-love-ranking',
        name: 'Brand Ranking',
        purpose: 'Compares average Brand Love across all tracked brands. Bar chart shows rankings for date range and YTD.',
        dataSource: 'topics_{org_id}.feather (same calculation as Brand Love Index)',
        metrics: ['brand_love'],
        transformation: 'Same as Brand Love Index: calculate from topics, then transform with sign(raw) * sqrt(abs(raw)) * 1000 / 13000.'
      },
      {
        id: 'dei-index',
        name: 'DEI Index',
        purpose: 'Shows DEI Brand Love score trends over time. Uses the same Brand Love calculation but only for DEI-related posts (DEI_flag == 1 in topics data). Chart Y-axis shows 0-60 range, same as Brand Love.',
        dataSource: 'topics_{org_id}.feather filtered by DEI_flag == 1 (calculate DEI Brand Love from this, see system instructions)',
        metrics: ['brand_love'],
        calculationDescription: 'DEI Index is Brand Love calculated only from DEI-flagged posts. Same calculation: 30-day rolling engagement score, then transformed for display.',
        transformation: 'Same as Brand Love: filter topics by DEI_flag==1, group by Brand+Date, 30-day rolling sum of post_engscore / 30, multiply by 40, then transform: display = sign(raw) * sqrt(abs(raw)) * 1000 / 13000.'
      },
      {
        id: 'brand-breakdown',
        name: 'Brand Breakdown',
        purpose: 'Shows content distribution by brand across different topics/pillars. Helps identify which brands are active in which content areas.',
        dataSource: 'topics_{org_id}.feather',
        metrics: ['topic_count', 'engagement_by_topic']
      },
      {
        id: 'platform-breakdown',
        name: 'Platform Breakdown',
        purpose: 'Compares performance across social media platforms (Instagram, TikTok, LinkedIn, etc.). Shows where engagement is strongest.',
        dataSource: 'topics_{org_id}.feather',
        metrics: ['engagement_by_platform', 'post_count_by_platform']
      },
      {
        id: 'top-posts',
        name: 'Top Posts',
        purpose: 'Displays the highest-performing posts by engagement score. Shows actual content that resonated with audiences.',
        dataSource: 'posts_{org_id}.feather',
        metrics: ['post_engscore', 'reactions', 'comments', 'shares']
      }
    ]
  },
  
  'keyword-analysis': {
    id: 'keyword-analysis',
    name: 'Keyword Analysis',
    description: 'Analyze keywords extracted from social media content. Explore word frequency, relevance to engagement, and sentiment patterns.',
    charts: [
      {
        id: 'keyword-syntax',
        name: 'Keyword Visualization',
        purpose: 'Interactive bubble chart and table showing top 100 keywords. Bubble size represents frequency, position shows sentiment (x-axis) vs relevance (y-axis). Click keywords to filter the message preview.',
        dataSource: 'topics_{org_id}.feather',
        metrics: ['frequency', 'relevance', 'sentiment'],
        calculationDescription: 'Relevance = correlation with engagement. Sentiment = average sentiment score of posts containing the keyword.'
      },
      {
        id: 'message-preview',
        name: 'Message Preview',
        purpose: 'Shows actual posts and comments containing selected keywords. Allows filtering by post type (posts vs comments). Includes direct links to original social media content.',
        dataSource: 'posts_{org_id}.feather, comments_{org_id}.parquet',
        metrics: ['likes', 'post_datetime']
      }
    ]
  },
  
  'image-comparison': {
    id: 'image-comparison',
    name: 'Image Comparison',
    description: 'Analyze Instagram visual content performance using computer vision. Discover which visual elements correlate with higher engagement.',
    charts: [
      {
        id: 'image-features',
        name: 'Brand Top Features',
        purpose: 'Shows which visual elements detected by AI (people, nature, products, text, etc.) appear in high-performing Instagram posts. Helps inform visual content strategy.',
        dataSource: 'vision_{org_id}.feather, vision_label_mapping_{org_id}.feather',
        metrics: ['engscore', 'label_frequency'],
        calculationDescription: 'Visual labels are detected by computer vision. Features are ranked by average engagement score of posts containing them.'
      }
    ]
  },
  
  'pillar-breakdown': {
    id: 'pillar-breakdown',
    name: 'Pillar Breakdown',
    description: 'Analyze content performance by pillar (content theme/category). Compare how different content strategies perform. Each post in topics has boolean pillar columns (e.g. "DEI", "Academic Excellence") and corresponding {pillar}_engscore columns.',
    charts: [
      {
        id: 'pillar-engagement-over-time',
        name: 'Pillar Engagement Over Time',
        purpose: 'Stacked/grouped bar chart showing pillar engagement by month. Has "Average Engagement" and "Total Engagement" toggle.',
        dataSource: 'topics_{org_id}.feather',
        metrics: ['avg_engagement', 'sum_engagement', 'post_count'],
        calculationDescription: 'For each pillar: 1) Filter topics to rows where {pillar_name} column == True (each pillar is a boolean column). 2) Group filtered posts by month. 3) Average Engagement = mean of {pillar_name}_engscore for ONLY those pillar-matching posts. Total Engagement = sum of {pillar_name}_engscore. CRITICAL: Average must be computed over pillar-matching posts only, NOT all posts. Posts with that pillar == False should be excluded before computing the mean.'
      },
      {
        id: 'pillar-comparison',
        name: 'Brand Pillar Comparison',
        purpose: 'Side-by-side daily time series charts. User selects a specific Brand AND Pillar for each chart, then toggles between Post Volume, Total Engagement, or Average Sentiment. Allows comparing two brand+pillar combos.',
        dataSource: 'topics_{org_id}.feather',
        metrics: ['post_volume', 'total_engagement', 'average_sentiment'],
        calculationDescription: 'For a selected brand+pillar: 1) Filter to brand. 2) Filter to posts where {pillar} == True. 3) Group by day. 4) Post Volume = count of posts per day. Total Engagement = sum of {pillar}_engscore per day. Uses {pillar}_engscore (pillar-attributed engagement), NOT post_engscore.'
      },
      {
        id: 'pillar-engagement-month',
        name: 'Engagement by Pillar (% share)',
        purpose: 'Stacked percentage bar chart showing what % of total engagement each pillar accounts for per month, normalized to 100%. Uses {pillar}_engscore columns (pillar-attributed engagement), NOT post_engscore.',
        dataSource: 'topics_{org_id}.feather',
        metrics: ['engagement_percentage'],
        calculationDescription: 'For each month: 1) Sum {pillar}_engscore for each pillar (filter to pillar-matching posts). 2) Divide each pillar sum by the TOTAL of ALL pillar sums for that month. 3) Result is a percentage that sums to 100% across all pillars. CRITICAL: Use {pillar}_engscore columns, NOT post_engscore. Including "No Topic" pillar in the total.'
      }
    ]
  },
  
  'employee-brand': {
    id: 'employee-brand',
    name: 'Employee/Personal Branding',
    description: 'Track personal branding metrics for individual employees or thought leaders associated with the organization.',
    charts: [
      {
        id: 'personal-metrics',
        name: 'Personal Metrics',
        purpose: 'Shows individual engagement metrics and performance trends for employee accounts.',
        dataSource: 'posts_{org_id}.feather',
        metrics: ['personal_engagement', 'follower_growth']
      },
      {
        id: 'audience-sentiment',
        name: 'Audience Sentiment',
        purpose: 'Analyzes sentiment in comments and reactions to personal posts.',
        dataSource: 'comments_{org_id}.parquet',
        metrics: ['sentiment_score', 'positive_ratio']
      }
    ]
  }
};

/**
 * Default page definition for unknown routes
 */
export const DEFAULT_PAGE: PageDefinition = {
  id: 'dashboard',
  name: 'Dashboard',
  description: 'General dashboard view. Use the navigation to explore specific analysis pages.',
  charts: []
};

/**
 * Get page definition by route path
 * 
 * @param pathname - Current route pathname
 * @returns PageDefinition for the current page
 */
export function getPageFromRoute(pathname: string): PageDefinition {
  // Remove org ID prefix and normalize
  const normalizedPath = pathname.toLowerCase();
  
  // Match against known page patterns
  if (normalizedPath.includes('brand-tracker') || normalizedPath.match(/\/\d+$/)) {
    // Root org page defaults to brand tracker
    return PAGE_REGISTRY['brand-tracker'];
  }
  if (normalizedPath.includes('keyword-analysis') || normalizedPath.includes('keywords')) {
    return PAGE_REGISTRY['keyword-analysis'];
  }
  if (normalizedPath.includes('image-comparison') || normalizedPath.includes('image')) {
    return PAGE_REGISTRY['image-comparison'];
  }
  if (normalizedPath.includes('pillar-breakdown') || normalizedPath.includes('pillar')) {
    return PAGE_REGISTRY['pillar-breakdown'];
  }
  if (normalizedPath.includes('employee-brand') || normalizedPath.includes('personal')) {
    return PAGE_REGISTRY['employee-brand'];
  }
  
  return DEFAULT_PAGE;
}

